import * as React from 'react';
import { Tooltip } from '@cian/ui-kit';
import classnames from 'classnames';

import { ICoordinates } from '../../../../types/newbuilding/genplan';
import { HouseTitle } from '../HouseTitle';
import { Portal } from '../Portal';

import * as styles from './styles.css';

interface IHouseProps {
  id: number;
  name: string;
  isReliable: boolean;
  isEscrow: boolean;
  isSalesStart?: boolean;
  tooltipPoint: ICoordinates;
  polygonPath: string;
  emptyStats: boolean;
  showTitle: boolean;
  containerWidth: number;
  containerHeight: number;
  tooltip: React.ReactNode;
  onHover(houseId: number): void;
}

export function House({
  id,
  name,
  isReliable,
  isEscrow,
  isSalesStart,
  tooltipPoint,
  polygonPath,
  emptyStats,
  showTitle,
  containerWidth,
  containerHeight,
  tooltip,
  onHover,
}: IHouseProps) {
  const [hovered, changeHovered] = React.useState(false);

  const { x, y } = tooltipPoint;
  const relativeX = (x / containerWidth) * 100;
  const relativeY = (y / containerHeight) * 100;

  React.useEffect(() => {
    if (hovered) {
      onHover(id);
    }
  }, [hovered, id, onHover]);

  const setHovered = React.useCallback(() => {
    changeHovered(true);
  }, [changeHovered]);
  const clearHovered = React.useCallback(() => {
    changeHovered(false);
  }, [changeHovered]);

  return (
    <>
      <Portal>
        <path
          className={classnames(styles['polygon'], hovered && styles['polygon-hovered'])}
          d={`M ${polygonPath} Z`}
          onMouseEnter={setHovered}
          onMouseLeave={clearHovered}
        />
      </Portal>
      <div
        className={styles['pin']}
        style={{
          left: `${relativeX}%`,
          top: `${relativeY}%`,
        }}
        onMouseEnter={setHovered}
        onMouseLeave={clearHovered}
      >
        <Tooltip open={hovered} theme="white" title={tooltip}>
          <div
            className={classnames(
              styles['point'],
              hovered && styles['point-hovered'],
              emptyStats && styles['point-gray'],
            )}
          />
        </Tooltip>
        {showTitle && !hovered && (
          <HouseTitle
            name={name}
            gray={emptyStats}
            isReliable={isReliable}
            isEscrow={isEscrow}
            isSalesStart={isSalesStart}
          />
        )}
      </div>
    </>
  );
}
